import type {
  Collection, Donation, Expense, LedgerEntry, BookEntry, AuditEntry,
  PeriodSummary, HeadTotal, HeadType, PaymentMode, Numeric
} from '../../types';
// Accounts has no store of its own — it is a read-only view that folds
// collections, donations and expenses into one double-entry-style ledger.

const amount = (v?: Numeric | null): number => parseFloat(String(v)) || 0;

// Donations record their channel in `purpose`; expenses only hint at it in
// free text. Anything not recognised as a bank movement is treated as cash.
const BANK_PURPOSES = ['UPI', 'Bank Transfer', 'Payment Gateway'];
const BANK_WORDS = ['bank', 'upi', 'transfer', 'cheque', 'online'];

const expenseMode = (description?: string): PaymentMode => {
  const text = String(description || '').toLowerCase();
  return BANK_WORDS.some((w) => text.includes(w)) ? 'Bank' : 'Cash';
};

export interface LedgerSources {
  collections: Collection[];
  donations: Donation[];
  expenses: Expense[];
}

export const buildLedger = ({ collections, donations, expenses }: LedgerSources): LedgerEntry[] => {
  const rows = [
    ...collections.map((c) => ({
      id: c.receiptId as string, rawId: c.id, date: c.date, type: 'Income' as const,
      head: c.source, description: c.givenBy || c.source,
      amount: amount(c.amount), mode: 'Cash' as const
    })),
    ...donations.map((d) => ({
      id: d.receiptNo as string, rawId: d.id, date: d.date, type: 'Income' as const,
      head: d.purpose, description: d.donorName,
      amount: amount(d.amount), mode: (BANK_PURPOSES.includes(d.purpose as string) ? 'Bank' : 'Cash') as PaymentMode
    })),
    ...expenses.map((e) => ({
      id: e.internalId as string, rawId: e.id, date: e.date, type: 'Expense' as const,
      head: e.category, description: e.expenseName,
      amount: amount(e.amount), mode: expenseMode(e.expenseName)
    }))
  ];

  return rows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

export const buildAuditLog = ({ collections, donations, expenses }: LedgerSources): AuditEntry[] => [
  ...collections.map((d) => ({ ...d, auditType: 'Collection' as const, origId: d.receiptId })),
  ...donations.map((d) => ({ ...d, auditType: 'Donation' as const, origId: d.receiptNo })),
  ...expenses.map((d) => ({ ...d, auditType: 'Expense' as const, origId: d.internalId }))
].sort((a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime());

export const REPORT_PERIODS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

export const periodRange = (
  period: string,
  customStart?: string,
  customEnd?: string
): { start: Date; end: Date } => {
  const today = new Date();
  let start = new Date();
  let end = new Date();

  if (period === 'day') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
  } else if (period === 'week') {
    start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
  } else if (period === 'month') {
    start = new Date(today.getFullYear(), today.getMonth(), 1);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);
  } else if (period === 'year') {
    start = new Date(today.getFullYear(), 0, 1);
    end = new Date(today.getFullYear(), 11, 31);
    end.setHours(23, 59, 59, 999);
  } else {
    start = customStart ? new Date(customStart) : new Date('2000-01-01');
    end = customEnd ? new Date(customEnd) : new Date('2100-01-01');
    end.setHours(23, 59, 59, 999);
  }

  return { start, end };
};

const signed = (row: LedgerEntry): number => (row.type === 'Income' ? row.amount : -row.amount);
const netFor = (rows: LedgerEntry[], mode: PaymentMode): number => rows.filter((r) => r.mode === mode).reduce((sum, r) => sum + signed(r), 0);

// Opening balances are everything before the period; closing rolls the
// period's movements onto them.
export const summarisePeriod = (
  ledger: LedgerEntry[],
  start: Date,
  end: Date
): PeriodSummary => {
  const inPeriod = ledger.filter((r) => {
    const d = new Date(r.date);
    return d >= start && d <= end;
  });
  const before = ledger.filter((r) => new Date(r.date) < start);

  const openingCash = netFor(before, 'Cash');
  const openingBank = netFor(before, 'Bank');
  const totalIncome = inPeriod.filter((r) => r.type === 'Income').reduce((s, r) => s + r.amount, 0);
  const totalExpense = inPeriod.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
  const surplusDeficit = totalIncome - totalExpense;

  return {
    inPeriod,
    openingCash,
    openingBank,
    openingFund: openingCash + openingBank,
    totalIncome,
    totalExpense,
    surplusDeficit,
    closingCash: openingCash + netFor(inPeriod, 'Cash'),
    closingBank: openingBank + netFor(inPeriod, 'Bank'),
    closingFund: openingCash + openingBank + surplusDeficit
  };
};

export const headWiseSummary = (
  rows: LedgerEntry[]
): { income: HeadTotal[]; expense: HeadTotal[] } => {
  const totals: Record<HeadType, Record<string, number>> = { Income: {}, Expense: {} };
  rows.forEach((r) => { totals[r.type][r.head] = (totals[r.type][r.head] || 0) + r.amount; });

  const toArray = (obj: Record<string, number>): HeadTotal[] =>
    Object.entries(obj).map(([head, total]) => ({ head, amount: total })).sort((a, b) => b.amount - a.amount);

  return { income: toArray(totals.Income), expense: toArray(totals.Expense) };
};

/** One line of the trial balance; exactly one of debit/credit is populated. */
export interface TrialBalanceRow {
  label: string;
  debit?: number;
  credit?: number;
  /** Section headings span the full width and carry no amounts. */
  isSection?: boolean;
}

export interface TrialBalance {
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  /** Dr and Cr agree — the double-entry parity check. */
  balanced: boolean;
}

/**
 * Opening assets (cash, bank) sit on the debit side against the accumulated
 * fund on the credit side; period expenses are debits and income credits.
 * Both columns therefore total openingFund + the period's other side.
 */
export const buildTrialBalance = (
  summary: PeriodSummary,
  heads: { income: HeadTotal[]; expense: HeadTotal[] }
): TrialBalance => {
  const rows: TrialBalanceRow[] = [
    { label: 'Opening Balances', isSection: true },
    { label: 'Cash in Hand', debit: summary.openingCash },
    { label: 'Cash at Bank', debit: summary.openingBank },
    { label: 'Accumulated General Fund', credit: summary.openingFund },
    { label: 'Revenue Accounts (Period Activity)', isSection: true },
    ...heads.expense.map((h) => ({ label: h.head, debit: h.amount })),
    ...heads.income.map((h) => ({ label: h.head, credit: h.amount }))
  ];

  // Each total is the sum of the column actually printed above it. totalDebit
  // used to start from openingFund — the credit side's figure — so the Debit
  // total disagreed with its own rows whenever opening assets (cash + bank)
  // and the opening fund differed, and `balanced` was true by construction
  // rather than by checking anything (CHM-165).
  const totalDebit = summary.openingCash + summary.openingBank + summary.totalExpense;
  const totalCredit = summary.openingFund + summary.totalIncome;

  return {
    rows,
    totalDebit,
    totalCredit,
    balanced: Math.abs(totalDebit - totalCredit) < 0.01
  };
};

/** Threshold above which a single transaction is flagged for review. */
export const HIGH_VALUE_THRESHOLD = 50000;

export interface AuditAlerts {
  highValueCount: number;
  voidedCount: number;
}

/** Counts for the "Internal Audit Alerts" panel, scoped to the report period. */
export const buildAuditAlerts = (
  inPeriod: LedgerEntry[],
  auditLog: AuditEntry[],
  start: Date,
  end: Date
): AuditAlerts => ({
  highValueCount: inPeriod.filter((r) => r.amount > HIGH_VALUE_THRESHOLD).length,
  voidedCount: auditLog.filter((a) => {
    const deleted = new Date(a.deletedAt);
    return !isNaN(deleted.getTime()) && deleted >= start && deleted <= end;
  }).length
});

// Oldest first, carrying a running balance from the opening figure.
export const buildBook = (
  rows: LedgerEntry[],
  mode: PaymentMode,
  openingBalance: number
): BookEntry[] => {
  let balance = openingBalance;
  return rows
    .filter((r) => r.mode === mode)
    .slice()
    .reverse()
    .map((r) => {
      balance += signed(r);
      return { ...r, runningBalance: balance };
    });
};
